import { doc, getDoc, setDoc, serverTimestamp } from 'firebase/firestore';
import { db } from '../config/firebase';
import type { UserProfile } from '@kaeliux/shared';
import { useUniverseStore } from '../store/universeStore';
import { authedPost } from './http';

export interface ProfileInput {
    displayName: string;
    email: string;
    settings?: UserProfile['settings'];
}

/**
 * Reads `users/{uid}` (global identity/settings) and, once a universe has
 * been picked this session, `universes/{u}/players/{uid}` (per-universe
 * progress) in parallel and merges them into the client-facing `UserProfile`
 * shape — see that type's doc comment in `@kaeliux/shared`'s
 * `user.schemas.ts` for why the merge happens here rather than each field
 * living wherever is most convenient to read.
 *
 * Called immediately on login, BEFORE `UniverseSelection` has run (issue
 * #53) — at that point there's no selected universe yet, so this returns the
 * identity-only profile (no `race`/`mainPlanetId`/tutorial fields). That's
 * fine: `App.tsx` gates on `!selectedUniverseId` before it ever looks at
 * `profile.race`.
 *
 * `universeIdOverride` lets `UniverseSelection` fetch the profile for the
 * universe the player is ABOUT to enter before committing that choice to
 * `useUniverseStore` — see its doc comment for why the fetch and the commit
 * must not be two separate ticks (a fixed data-integrity bug, not style).
 * Every other caller omits it and falls back to whatever's already selected.
 */
export async function getUserProfile(uid: string, universeIdOverride?: string): Promise<UserProfile | null> {
    const universeId = universeIdOverride ?? useUniverseStore.getState().selectedUniverseId;
    const [userSnap, playerSnap] = await Promise.all([
        getDoc(doc(db, 'users', uid)),
        universeId ? getDoc(doc(db, 'universes', universeId, 'players', uid)) : Promise.resolve(null),
    ]);
    if (!userSnap.exists()) return null;
    const data = userSnap.data();
    const playerData = playerSnap?.exists() ? playerSnap.data() : undefined;
    return {
        ...data,
        id: uid,
        createdAt: data.createdAt?.toDate() ?? new Date(),
        lastLoginAt: data.lastLoginAt?.toDate() ?? new Date(),
        race: playerData?.race,
        mainPlanetId: playerData?.mainPlanetId,
        tutorialStepIndex: playerData?.tutorialStepIndex,
        tutorialDisabled: playerData?.tutorialDisabled,
    } as UserProfile;
}

export async function createOrUpdateProfile(uid: string, data: ProfileInput): Promise<void> {
    await setDoc(
        doc(db, 'users', uid),
        {
            ...data,
            settings: data.settings ?? {
                language: 'en',
                emailNotifications: true,
                inGameNotifications: true,
            },
            createdAt: serverTimestamp(),
            lastLoginAt: serverTimestamp(),
        },
        { merge: true }
    );
}

export async function touchLastLogin(uid: string): Promise<void> {
    await setDoc(doc(db, 'users', uid), { lastLoginAt: serverTimestamp() }, { merge: true });
}

/** Records the email a guest account gained when it was upgraded, without touching its display name. */
export async function updateProfileEmail(uid: string, email: string): Promise<void> {
    await setDoc(doc(db, 'users', uid), { email }, { merge: true });
}

/**
 * Rename the player's nickname (`displayName`). Goes through a Cloud
 * Function rather than a plain client write — `displayName` is denormalized
 * onto the server-owned `players/{uid}` doc and the public
 * `universes/{u}/rankings/{uid}` doc, and the function keeps all three in
 * sync (see `updateDisplayName` in `apps/functions/src/index.ts`).
 */
export async function updateDisplayName(_uid: string, displayName: string): Promise<void> {
    await authedPost<unknown>('/updateDisplayName', { displayName });
}

function blobToDataUrl(blob: Blob): Promise<string> {
    return new Promise((resolve, reject) => {
        const reader = new FileReader();
        reader.onload = () => resolve(reader.result as string);
        reader.onerror = () => reject(reader.error);
        reader.readAsDataURL(blob);
    });
}

/**
 * Upload a (already client-resized) profile photo. The whole write — Cloud
 * Storage object plus the `photoURL` denormalized onto `users/{uid}`,
 * `players/{uid}`, and the public `universes/{u}/rankings/{uid}` doc —
 * happens server-side in the `uploadProfilePhoto` Cloud Function; the client
 * never talks to Cloud Storage directly (see `apps/functions/src/index.ts`).
 */
export async function uploadProfilePhoto(_uid: string, photo: Blob): Promise<string> {
    const photoData = await blobToDataUrl(photo);
    const { photoURL } = await authedPost<{ photoURL: string }>('/uploadProfilePhoto', { photoData });
    return photoURL;
}

/**
 * Persist tutorial step/disabled state. Goes through a Cloud Function rather
 * than a plain client write — tutorial progress lives on the server-owned
 * `universes/{u}/players/{uid}` doc (see `updateTutorialProgress` in
 * `apps/functions/src/index.ts`), not the client-writable `users/{uid}` doc,
 * so a player can't forge "tutorial finished" state. `planetId` is the
 * planet on screen: the server judges the steps being passed against it and
 * answers 412 for a move past a step that isn't complete.
 */
export async function updateTutorialProgress(
    _uid: string,
    progress: { stepIndex: number; disabled: boolean; planetId?: string }
): Promise<void> {
    await authedPost<unknown>('/updateTutorialProgress', {
        stepIndex: progress.stepIndex,
        disabled: progress.disabled,
        ...(progress.planetId ? { planetId: progress.planetId } : {}),
    });
}
